import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Pencil, X, Check, GripVertical, ToggleLeft, ToggleRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OperationTypes() {
  const { selectedTenantId } = useAuth();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formName, setFormName] = useState('');
  const [formOrder, setFormOrder] = useState('');

  const fetchTypes = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/operation-types/all`, {
        headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': selectedTenantId }
      });
      if (res.ok) setTypes(await res.json());
    } catch (err) {
      toast.error('Erro ao carregar tipos de operação');
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId]);

  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  const handleSave = async () => {
    if (!formName.trim()) { toast.error('Nome é obrigatório'); return; }
    const token = localStorage.getItem('token');
    const body = { nome: formName.trim() };
    if (formOrder) body.ordem_exibicao = parseInt(formOrder);

    try {
      const url = editingId ? `${API_URL}/api/operation-types/${editingId}` : `${API_URL}/api/operation-types`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'X-Tenant-Id': selectedTenantId },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        toast.success(editingId ? 'Tipo atualizado' : 'Tipo criado');
        resetForm();
        fetchTypes();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao salvar');
      }
    } catch { toast.error('Erro de conexão'); }
  };

  const toggleActive = async (type) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/operation-types/${type.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'X-Tenant-Id': selectedTenantId },
        body: JSON.stringify({ ativo: !type.ativo })
      });
      if (res.ok) {
        toast.success(type.ativo ? 'Tipo inativado' : 'Tipo ativado');
        fetchTypes();
      }
    } catch { toast.error('Erro ao atualizar'); }
  };

  const startEdit = (type) => {
    setEditingId(type.id);
    setFormName(type.nome);
    setFormOrder(String(type.ordem_exibicao));
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormName('');
    setFormOrder('');
  };

  if (loading) return <div className="p-6 text-gray-400" data-testid="operation-types-loading">Carregando...</div>;

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto" data-testid="operation-types-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-xl font-bold text-white">Tipos de Operação</h1>
          <p className="text-sm text-gray-500 mt-1">Gerencie os tipos disponíveis no formulário de O.S.</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }}
          className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg font-medium text-sm flex items-center gap-2"
          data-testid="add-operation-type-btn">
          <Plus size={16} /> Novo Tipo
        </button>
      </div>

      {showForm && (
        <div className="bg-[#1A1A1A] border border-gray-800 rounded-lg p-4 mb-6" data-testid="operation-type-form">
          <h3 className="text-white font-medium mb-3">{editingId ? 'Editar Tipo' : 'Novo Tipo de Operação'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <input type="text" value={formName} onChange={(e) => setFormName(e.target.value)}
                placeholder="Nome do tipo de operação"
                className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                data-testid="input-operation-type-name" />
            </div>
            <div>
              <input type="number" value={formOrder} onChange={(e) => setFormOrder(e.target.value)}
                placeholder="Ordem"
                className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                data-testid="input-operation-type-order" />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={handleSave}
              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg font-medium text-sm flex items-center gap-1"
              data-testid="save-operation-type-btn">
              <Check size={14} /> Salvar
            </button>
            <button onClick={resetForm}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm flex items-center gap-1"
              data-testid="cancel-operation-type-btn">
              <X size={14} /> Cancelar
            </button>
          </div>
        </div>
      )}

      <div className="bg-[#1A1A1A] border border-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="text-left text-xs text-gray-500 font-medium px-4 py-3 w-10">#</th>
              <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Nome</th>
              <th className="text-center text-xs text-gray-500 font-medium px-4 py-3">Status</th>
              <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {types.map((type) => (
              <tr key={type.id} className="border-b border-gray-800/50 hover:bg-[#111]" data-testid={`operation-type-row-${type.id}`}>
                <td className="px-4 py-3">
                  <GripVertical size={14} className="text-gray-600" />
                </td>
                <td className="px-4 py-3">
                  <span className={`text-sm ${type.ativo ? 'text-white' : 'text-gray-500 line-through'}`}>{type.nome}</span>
                  <span className="text-xs text-gray-600 ml-2">#{type.ordem_exibicao}</span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-xs px-2 py-1 rounded-full ${type.ativo ? 'bg-green-500/10 text-green-400' : 'bg-gray-700/50 text-gray-500'}`}>
                    {type.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => startEdit(type)} title="Editar"
                      className="p-1.5 hover:bg-gray-800 rounded text-gray-400 hover:text-yellow-500"
                      data-testid={`edit-operation-type-${type.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => toggleActive(type)} title={type.ativo ? 'Inativar' : 'Ativar'}
                      className={`p-1.5 hover:bg-gray-800 rounded ${type.ativo ? 'text-green-400 hover:text-red-400' : 'text-gray-500 hover:text-green-400'}`}
                      data-testid={`toggle-operation-type-${type.id}`}>
                      {type.ativo ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {types.length === 0 && (
              <tr><td colSpan={4} className="text-center text-gray-500 py-8 text-sm">Nenhum tipo de operação cadastrado</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
